// =============================================================================
// PitchConfiguration.jsx - PRODUCTION READY with Multi-Facility Support
// =============================================================================
// Complete component with facility selector, modals, handlers, and subscription tier limits
// MULTI-FACILITY: Supports selecting facility and showing pitches per facility
// =============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import PremiumFeatureModal from '../PremiumFeatureModal';
import TrialExpiredModal from '../TrialExpiredModal';

// Import configuration constants
import {
  PITCH_CONFIG
} from '../../utils/settings/settingsConfig';

// Import subscription limits from YOUR subscriptionTiers.js file
import {
  getLimitsForTier,
  getUpgradeTiers,
  getPitchLimitBenefits
} from '../../config/subscriptionTiers';

// =============================================================================
// PITCH ID UTILITIES
// =============================================================================
/**
 * Get the next sequential pitch number based on existing pitches
 */
const getNextPitchNumber = (existingPitches) => {
  if (!existingPitches || existingPitches.length === 0) {
    return 1;
  }
  
  const numbers = existingPitches.map(pitch => {
    const id = pitch.id || pitch.pitchId || '';
    const simpleMatch = id.match(/^pitch-(\d+)$/);
    if (simpleMatch) return parseInt(simpleMatch[1]);
    
    const timestampMatch = id.match(/pitch-\d+-(\d+)$/);
    if (timestampMatch) return parseInt(timestampMatch[1]);
    
    return 0;
  });
  
  return Math.max(...numbers, 0) + 1;
};

// =============================================================================
// CONFIGURATION CONSTANTS
// =============================================================================
const PITCH_SECTION_OPTIONS = [
  {
    id: '8-sections',
    name: '8 Sections',
    description: 'Full pitch divided into 8 sections (A,B,C,D,E,F,G,H)',
    icon: '▫',
    satelliteLayout: 'EIGHTH',
    sectionsCount: 8,
    RecommendedSize: 'Length: 100m Width: 60m',
    Agegroup: 'Under 14+'
  },
  {
    id: '4-sections',
    name: '4 Sections', 
    description: 'Half Pitch divided into 4 quadrants',
    icon: '⬜',
    satelliteLayout: 'HALF',
    sectionsCount: 4,
    RecommendedSize: 'Length: 65m Width: 45m',
    Agegroup: 'Under 10-13s'
  },
  {
    id: '2-sections',
    name: '2 Sections',
    description: 'Quarter Pitch divided into 2 halves',
    icon: '▬',
    satelliteLayout: 'QUARTER',
    sectionsCount: 2,
    RecommendedSize: 'Length: 45m Width: 35m',
    Agegroup: 'Under 8-9s'
  },
  {
    id: '1-section',
    name: '1 Section',
    description: 'One small single section',
    icon: '⬛',
    satelliteLayout: 'FULL',
    sectionsCount: 1,
    RecommendedSize: 'Length: 30m Width: 20m',
    Agegroup: 'Under 6-7s'
  }
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================
const convertSatelliteConfigToSettingsFormat = (satelliteConfig) => {
  if (typeof satelliteConfig === 'string') {
    return satelliteConfig;
  }
  
  if (typeof satelliteConfig === 'object' && satelliteConfig !== null) {
    const { sectionLayout, sectionsCount } = satelliteConfig;
    
    const option = PITCH_SECTION_OPTIONS.find(opt => 
      opt.satelliteLayout === sectionLayout || opt.sectionsCount === sectionsCount
    );
    
    if (option) {
      return option.id;
    }
    
    switch (sectionsCount) {
      case 8: return '8-sections';
      case 4: return '4-sections';
      case 2: return '2-sections';
      case 1: return '1-section';
      default: return '8-sections';
    }
  }
  
  return '8-sections';
};

const convertSettingsFormatToSatelliteConfig = (settingsFormat, pitchId, existingConfig = null, allPitches = []) => {
  const option = PITCH_SECTION_OPTIONS.find(opt => opt.id === settingsFormat);
  if (!option) return existingConfig;
  
  const generateSectionLabels = (count) => {
    const labels = [];
    for (let i = 0; i < count; i++) {
      labels.push(String.fromCharCode(65 + i));
    }
    return labels;
  };
  
  // Generate default name based on pitch number
  let defaultName = 'Pitch';
  
  // Try to extract number from simple format: pitch-1, pitch-2
  const simpleMatch = pitchId.match(/^pitch-(\d+)$/);
  if (simpleMatch) {
    defaultName = `Pitch ${simpleMatch[1]}`;
  } else {
    // Fallback: calculate from array position
    const pitchNumber = getNextPitchNumber(allPitches);
    defaultName = `Pitch ${pitchNumber}`;
  }
  
  return {
    pitchId: pitchId,
    customName: existingConfig?.customName || defaultName,
    sectionLayout: option.satelliteLayout,
    layoutName: option.name,
    sectionsCount: option.sectionsCount,
    sectionLabels: generateSectionLabels(option.sectionsCount),
    updatedAt: new Date().toISOString()
  };
};

const normalizePitchId = (id) => {
  if (!id) return '';
  const idStr = String(id);
  
  // Remove any duplicate "pitch" prefixes to prevent pitchpitch-xxx
  // Match: pitch-pitch-xxx, pitchpitch-xxx, pitch-xxx
  const cleaned = idStr.replace(/^(pitch-?)+/i, 'pitch-');
  
  return cleaned;
};

// =============================================================================
// PITCH SIZE WARNING MODAL
// =============================================================================
const PitchSizeWarningModal = ({ pendingChange, onConfirm, onCancel, isConfirmed, setIsConfirmed }) => {
  if (!pendingChange) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        width: '100%',
        maxWidth: '500px',
        boxShadow: '0 20px 25px rgba(0, 0, 0, 0.25)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '20px',
          gap: '12px'
        }}>
          <div style={{ fontSize: '32px', color: '#dc2626' }}>⚠️</div>
          <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#dc2626', margin: 0 }}>
            Warning: Pitch Size Change
          </h3>
        </div>

        <div style={{
          backgroundColor: '#fee2e2',
          border: '2px solid #dc2626',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <p style={{ fontSize: '14px', color: '#991b1b', margin: 0, fontWeight: '500' }}>
            Changing the pitch size configuration may affect:
          </p>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#991b1b', fontSize: '14px' }}>
            <li>Existing pitch allocations</li>
            <li>Scheduled training sessions</li>
            <li>Match day bookings</li>
          </ul>
        </div>

        <div style={{
          backgroundColor: '#f3f4f6',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <p style={{ fontSize: '14px', color: '#374151', margin: 0 }}>
            <strong>{pendingChange.pitchName}</strong> will change from <strong>{pendingChange.oldConfig}</strong> to <strong>{pendingChange.newConfig}</strong>
          </p>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={isConfirmed}
              onChange={(e) => setIsConfirmed(e.target.checked)}
              style={{ width: '18px', height: '18px', marginTop: '2px' }}
            />
            <span style={{ fontSize: '14px', color: '#374151', lineHeight: '1.5' }}>
              I understand that this change may affect existing allocations and I have verified there are no conflicts.
            </span>
          </label>
        </div>

        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              backgroundColor: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!isConfirmed}
            style={{
              padding: '10px 20px',
              backgroundColor: isConfirmed ? '#dc2626' : '#9ca3af',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: isConfirmed ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Confirm Change
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// PITCH CONFIG MODAL
// =============================================================================
const PitchConfigModal = ({ pitch, pitchName, currentConfig, onConfigUpdate, onClose }) => {
  const [selectedConfig, setSelectedConfig] = useState(currentConfig || '8-sections');
  
  const handleConfirm = () => {
    const pitchId = pitch.id || pitch.pitchId;
    onConfigUpdate(pitchId, selectedConfig);
  };

  const hasChanges = selectedConfig !== currentConfig;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1100,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 25px rgba(0, 0, 0, 0.25)'
      }}>
        <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', marginTop: 0, marginBottom: '8px' }}>
          Configure Pitch Sections
        </h3>
        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>
          Choose how <strong>{pitchName || 'this pitch'}</strong> should be divided for allocations
        </p>

        {/* Section Options */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
          {PITCH_SECTION_OPTIONS.map((option) => {
            const isSelected = selectedConfig === option.id;
            const isCurrent = currentConfig === option.id;
            
            return (
              <button
                key={option.id}
                onClick={() => setSelectedConfig(option.id)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '16px',
                  padding: '16px',
                  backgroundColor: isSelected ? '#f0f9ff' : 'white',
                  border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                  width: '100%'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }
                }}
              >
                <span style={{ fontSize: '24px', minWidth: '32px', textAlign: 'center' }}>
                  {option.icon}
                </span>
                
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: isSelected ? '#1e40af' : '#1f2937',
                    marginBottom: '4px'
                  }}>
                    {option.name}
                    {isCurrent && (
                      <span style={{ marginLeft: '8px', fontSize: '14px', color: '#10b981', fontWeight: '500' }}>
                        (Current)
                      </span>
                    )}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: isSelected ? '#1e40af' : '#6b7280',
                    marginBottom: '4px'
                  }}>
                    {option.description}
                  </div>
                  
                  <div style={{ fontSize: '13px', color: isSelected ? '#1e40af' : '#6b7280', marginBottom: '2px' }}>
                    <strong>Recommended Size:</strong> {option.RecommendedSize}
                  </div>
                  
                  <div style={{ fontSize: '13px', color: isSelected ? '#1e40af' : '#6b7280', marginBottom: '4px' }}>
                    <strong>Age Group:</strong> {option.Agegroup}
                  </div>
                  
                  <div style={{ fontSize: '12px', color: isSelected ? '#1e40af' : '#9ca3af' }}>
                    {option.sectionsCount} sections • {option.satelliteLayout} layout
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        
        {/* Warning Note */}
        <div style={{
          marginBottom: '24px',
          padding: '16px',
          backgroundColor: '#fef3c7',
          border: '1px solid #fde68a',
          borderRadius: '8px'
        }}>
          <p style={{ fontSize: '13px', color: '#92400e', margin: 0, fontWeight: '500' }}>
            Note: Changing the section configuration may affect existing pitch allocations. You may need to review and update scheduled matches and training sessions.
          </p>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          borderTop: '1px solid #e5e7eb',
          paddingTop: '20px'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!hasChanges}
            style={{
              padding: '10px 20px',
              backgroundColor: hasChanges ? '#3b82f6' : '#9ca3af',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: hasChanges ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              if (hasChanges) e.currentTarget.style.backgroundColor = '#2563eb';
            }}
            onMouseLeave={(e) => {
              if (hasChanges) e.currentTarget.style.backgroundColor = '#3b82f6';
            }}
          >
            {hasChanges ? 'Confirm Change' : 'No Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// SUCCESS CONFIRMATION MODAL
// =============================================================================
const SuccessConfirmationModal = ({ pitchName, configName, onClose }) => {
  if (!pitchName || !configName) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1200,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        width: '100%',
        maxWidth: '450px',
        boxShadow: '0 20px 25px rgba(0, 0, 0, 0.25)',
        textAlign: 'center'
      }}>
        {/* Styled Checkmark Icon */}
        <div style={{
          width: '64px',
          height: '64px',
          margin: '0 auto 20px',
          borderRadius: '50%',
          backgroundColor: '#10b981',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <svg 
            width="32" 
            height="32" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="white" 
            strokeWidth="3" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>
        
        <h3 style={{ 
          fontSize: '20px', 
          fontWeight: '600', 
          color: '#047857', 
          margin: '0 0 12px 0' 
        }}>
          Configuration Updated Successfully!
        </h3>
        
        <p style={{ 
          fontSize: '15px', 
          color: '#374151', 
          margin: '0 0 24px 0',
          lineHeight: '1.5'
        }}>
          <strong>{pitchName}</strong> has been updated to <strong>{configName}</strong>.
        </p>
        
        <button
          onClick={onClose}
          style={{
            padding: '12px 24px',
            backgroundColor: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: '600',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
        >
          Done
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN PITCH CONFIGURATION COMPONENT
// =============================================================================
const PitchConfiguration = ({
  pitchNames,
  pitchReferencePoints,
  pitchSectionConfigurations,
  configuredPitches,
  onPitchNamesChange,
  onReferencePointsChange,
  onSectionConfigChange,
  onSave,
  showSuccess,
  isSaving = false,
  isAdmin,
  clubInfo,
  user,
  subscriptionTier = 'free'
}) => {
  // ===========================================================================
  // STATE
  // ===========================================================================
  const [isExpanded, setIsExpanded] = useState(false);
  const [localReferencePoints, setLocalReferencePoints] = useState({});
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedPitchForConfig, setSelectedPitchForConfig] = useState(null);
  const [showSizeWarning, setShowSizeWarning] = useState(false);
  const [pendingSizeChange, setPendingSizeChange] = useState(null);
  const [sizeWarningConfirmed, setSizeWarningConfirmed] = useState(false);
  
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumModalConfig, setPremiumModalConfig] = useState({});
  
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState(null);
  
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);

  // ===========================================================================
  // MULTI-FACILITY STATE
  // ===========================================================================
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState(null);
  const [isLoadingFacilities, setIsLoadingFacilities] = useState(false);
  const [facilityPitches, setFacilityPitches] = useState([]);

  // ===========================================================================
  // LOAD FACILITIES
  // ===========================================================================
  useEffect(() => {
    const loadFacilities = async () => {
      if (!clubInfo?.clubId || !isExpanded) return;
      
      try {
        setIsLoadingFacilities(true);
        console.log('📦 [PitchConfiguration] Loading facilities for club:', clubInfo.clubId);
        
        const facilitiesRef = collection(db, 'clubs', clubInfo.clubId, 'facilities');
        const facilitiesSnap = await getDocs(facilitiesRef);
        
        const facilitiesData = [];
        facilitiesSnap.forEach((doc) => {
          facilitiesData.push({ id: doc.id, ...doc.data() });
        });
        
        // Sort: primary first, then alphabetically
        const primaryId = clubInfo.primaryFacilityId;
        facilitiesData.sort((a, b) => {
          if (a.isPrimary || a.id === primaryId) return -1;
          if (b.isPrimary || b.id === primaryId) return 1;
          return (a.name || '').localeCompare(b.name || '');
        });
        
        setFacilities(facilitiesData);
        console.log('✅ [PitchConfiguration] Loaded facilities:', facilitiesData.length);
        facilitiesData.forEach(f => {
          console.log(`🏟️ [PitchConfiguration] Facility: ${f.name}`, {
            id: f.id,
            facilityConfigured: f.facilityConfigured,
            isConfigured: f.isConfigured,
            hasSatelliteImage: !!f.satelliteImageUrl,
            pitchCount: f.pitches?.length || 0
          });
        });
        
        // Auto-select primary facility or first facility
        if (facilitiesData.length > 0 && !selectedFacilityId) {
          const primaryFacility = facilitiesData.find(f => f.isPrimary || f.id === primaryId) || facilitiesData[0];
          setSelectedFacilityId(primaryFacility.id);
        }
      } catch (err) {
        console.error('❌ [PitchConfiguration] Error loading facilities:', err);
      } finally {
        setIsLoadingFacilities(false);
      }
    };
    
    loadFacilities();
  }, [clubInfo?.clubId, clubInfo?.primaryFacilityId, isExpanded]);

  // ===========================================================================
  // UPDATE PITCHES WHEN FACILITY CHANGES
  // ===========================================================================
  useEffect(() => {
    if (!selectedFacilityId || facilities.length === 0) {
      // Fall back to legacy configuredPitches if no facilities
      setFacilityPitches(configuredPitches || []);
      return;
    }
    
    const selectedFacility = facilities.find(f => f.id === selectedFacilityId);
    if (selectedFacility && selectedFacility.pitches && selectedFacility.pitches.length > 0) {
      // Use pitches from the selected facility
      console.log('🏟️ [PitchConfiguration] Using pitches from facility:', selectedFacilityId, selectedFacility.pitches.length);
      setFacilityPitches(selectedFacility.pitches);
    } else {
      // If facility has no pitches, check if this is the primary facility and use legacy pitches
      const isPrimaryOrOnly = selectedFacility?.isPrimary || facilities.length === 1;
      if (isPrimaryOrOnly && configuredPitches && configuredPitches.length > 0) {
        console.log('🏟️ [PitchConfiguration] Using legacy pitches for primary facility');
        setFacilityPitches(configuredPitches);
      } else {
        setFacilityPitches([]);
      }
    }
  }, [selectedFacilityId, facilities, configuredPitches]);

  // ===========================================================================
  // COMPUTED VALUES
  // ===========================================================================
  const selectedFacility = facilities.find(f => f.id === selectedFacilityId);
  const displayPitches = facilityPitches;
  const hasFacilities = facilities.length > 0;
  
  // Calculate TOTAL pitch count across ALL facilities (for limit enforcement)
  const totalPitchCount = useMemo(() => {
    return facilities.reduce((total, facility) => {
      const pitchCount = facility.pitchCount || facility.pitches?.length || 0;
      return total + pitchCount;
    }, 0);
  }, [facilities]);

  // ===========================================================================
  // TRIAL CHECK
  // ===========================================================================
  const isTrialExpired = () => {
    if (!clubInfo) return false;
    
    const now = new Date();
    const trialEndsAt = clubInfo.trialEndsAt?.toDate ? 
      clubInfo.trialEndsAt.toDate() : 
      new Date(clubInfo.trialEndsAt);
    
    const subscriptionStatus = clubInfo.subscriptionStatus || 'trial';
    const msRemaining = trialEndsAt - now;
    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
    
    return subscriptionStatus === 'trial' && daysRemaining <= 0;
  };

  // ===========================================================================
  // SUBSCRIPTION LIMITS - Use TOTAL pitch count across all facilities
  // ===========================================================================
  const limits = getLimitsForTier(subscriptionTier);
  const currentPitchCount = totalPitchCount; // Use total, not just selected facility
  const canAddMorePitches = currentPitchCount < limits.maxPitches;
  const pitchesRemaining = limits.maxPitches - currentPitchCount;

  // ===========================================================================
  // HANDLERS
  // ===========================================================================
  const handleSatelliteNavigate = () => {
    if (!canAddMorePitches) {
      const upgradeTiers = getUpgradeTiers(subscriptionTier, 'pitches');
      setPremiumModalConfig({
        featureName: 'Additional Pitches',
        currentTier: subscriptionTier,
        requiredTiers: upgradeTiers.map(t => t.toLowerCase()),
        benefits: getPitchLimitBenefits(upgradeTiers)
      });
      setShowPremiumModal(true);
      return;
    }
    
    // Navigate to satellite with facility context
    if (selectedFacilityId) {
      window.location.href = `/satellite?facilityId=${selectedFacilityId}`;
    } else {
      window.location.href = '/satellite';
    }
  };

  const handleReferencePointChange = (pitchId, position, value) => {
    const normalizedId = normalizePitchId(pitchId);
    setLocalReferencePoints(prev => ({
      ...prev,
      [normalizedId]: {
        ...prev[normalizedId],
        [position]: value
      }
    }));
  };
  
  const handleReferencePointBlur = async (pitchId, position) => {
    const normalizedId = normalizePitchId(pitchId);
    const currentValue = localReferencePoints[normalizedId]?.[position] || '';
    const savedValue = pitchReferencePoints[normalizedId]?.[position] || '';
    
    if (currentValue !== savedValue) {
      const updated = {
        ...pitchReferencePoints,
        [normalizedId]: {
          ...pitchReferencePoints[normalizedId],
          [position]: currentValue
        }
      };
      onReferencePointsChange(updated);
      
      const success = await onSave(pitchNames, updated, pitchSectionConfigurations);
      if (success) {
        showSuccess('Reference point updated');
      }
    }
  };
  
  const openConfigModal = (pitch) => {
    setSelectedPitchForConfig(pitch);
    setShowConfigModal(true);
  };
  
  const closeConfigModal = () => {
    setShowConfigModal(false);
    setSelectedPitchForConfig(null);
  };
  
  const handleSectionConfigUpdate = async (pitchId, sectionConfig) => {
    const normalizedId = normalizePitchId(pitchId);
    const existingSatelliteConfig = pitchSectionConfigurations[normalizedId];
    
    if (existingSatelliteConfig) {
      const currentConfig = convertSatelliteConfigToSettingsFormat(existingSatelliteConfig);
      if (currentConfig !== sectionConfig) {
        // Close the config modal first
        closeConfigModal();
        
        // Then show warning
        setPendingSizeChange({
          pitchId: normalizedId,
          sectionConfig,
          pitchName: pitchNames[normalizedId] || `Pitch ${selectedPitchForConfig?.number || 'Unknown'}`,
          oldConfig: currentConfig,
          newConfig: sectionConfig
        });
        setSizeWarningConfirmed(false);
        setShowSizeWarning(true);
        return;
      }
    }
    
    proceedWithSizeChange(normalizedId, sectionConfig);
  };
  
  const proceedWithSizeChange = async (pitchId, sectionConfig) => {
    const existingSatelliteConfig = pitchSectionConfigurations[pitchId];
    const satelliteConfig = convertSettingsFormatToSatelliteConfig(
      sectionConfig, 
      pitchId, 
      existingSatelliteConfig
    );
    
    const updated = { ...pitchSectionConfigurations, [pitchId]: satelliteConfig };
    
    // Update local state if handler exists
    if (onSectionConfigChange && typeof onSectionConfigChange === 'function') {
      onSectionConfigChange(updated);
    }
    
    closeConfigModal();
    setShowSizeWarning(false);
    setPendingSizeChange(null);
    setSizeWarningConfirmed(false);
    
    const success = await onSave(pitchNames, pitchReferencePoints, updated, {
      lastUpdated: new Date().toISOString(),
      updatedBy: user?.email || 'unknown'
    });
    
    if (success) {
      const selectedOption = PITCH_SECTION_OPTIONS.find(option => option.id === sectionConfig);
      const pitchName = pitchNames[pitchId] || `Pitch ${selectedPitchForConfig?.number || 'Unknown'}`;
      
      // Show success modal instead of toast
      setSuccessModalData({
        pitchName,
        configName: selectedOption?.name || sectionConfig
      });
      setShowSuccessModal(true);
      
      // Also call showSuccess if it exists (for backwards compatibility)
      if (showSuccess) {
        showSuccess(`${pitchName} configuration updated to ${selectedOption?.name || sectionConfig}`);
      }
    }
  };

  return (
    <>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb'
      }}>
        <div
          onClick={() => {
            if (isTrialExpired()) {
              setShowTrialExpiredModal(true);
              return;
            }
            setIsExpanded(!isExpanded);
          }}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '8px',
            margin: '-8px',
            marginBottom: '8px',
            borderRadius: '8px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
              Pitch Configuration
            </h2>
          </div>
          <div style={{
            fontSize: '20px',
            color: '#6b7280',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
          }}>
            ▼
          </div>
        </div>

        <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
          Set up pitch names, sections, and surface types for your facilities
        </p>

        {isExpanded && (
          <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
            
            {/* ===============================================================
                FACILITY SELECTOR
                =============================================================== */}
            {isLoadingFacilities ? (
              <div style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                color: '#6b7280'
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid #e5e7eb',
                  borderTopColor: '#3b82f6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Loading facilities...
              </div>
            ) : hasFacilities ? (
              <div style={{
                padding: '16px',
                backgroundColor: '#f0fdf4',
                borderRadius: '8px',
                border: '1px solid #bbf7d0'
              }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#166534',
                  marginBottom: '8px'
                }}>
                  🏟️ Select Facility
                </label>
                <select
                  value={selectedFacilityId || ''}
                  onChange={(e) => setSelectedFacilityId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '1px solid #86efac',
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  {facilities.map((facility) => (
                    <option key={facility.id} value={facility.id}>
                      {facility.name}
                      {facility.isPrimary || facility.id === clubInfo?.primaryFacilityId ? ' (Primary)' : ''}
                      {' • '}
                      {(facility.pitches?.length || 0)} pitch{(facility.pitches?.length || 0) !== 1 ? 'es' : ''}
                    </option>
                  ))}
                </select>
                {selectedFacility && 
                  !selectedFacility.facilityConfigured && 
                  !selectedFacility.isConfigured && 
                  !selectedFacility.satelliteImageUrl &&
                  !(selectedFacility.pitches?.length > 0) && (
                  <p style={{
                    margin: '8px 0 0 0',
                    fontSize: '13px',
                    color: '#ca8a04'
                  }}>
                    ⚠️ This facility hasn't been configured with a satellite image yet.
                  </p>
                )}
              </div>
            ) : (
              <div style={{
                padding: '16px',
                backgroundColor: '#fef3c7',
                borderRadius: '8px',
                border: '1px solid #fde68a'
              }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#92400e' }}>
                  ⚠️ No facilities found. Add a facility in the <strong>Facilities</strong> section above to configure pitches.
                </p>
              </div>
            )}

            {/* ===============================================================
                PITCH COUNT & LIMITS
                =============================================================== */}
            <div style={{
              padding: '12px',
              backgroundColor: canAddMorePitches ? '#f0f9ff' : '#fef3c7',
              borderRadius: '6px',
              fontSize: '13px',
              color: canAddMorePitches ? '#0369a1' : '#d97706',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>
                {displayPitches.length} pitch{displayPitches.length !== 1 ? 'es' : ''} 
                {selectedFacility && ` in ${selectedFacility.name}`}
                {facilities.length > 1 && ` • ${currentPitchCount} total across all facilities`}
                {' • '}
                {pitchesRemaining} of {limits.maxPitches} available
              </span>
              {!canAddMorePitches && (
                <span style={{ 
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  ⚠️ Limit Reached
                </span>
              )}
            </div>

            {/* ===============================================================
                ADD PITCH BUTTON
                =============================================================== */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <button
                onClick={handleSatelliteNavigate}
                disabled={!isAdmin || !hasFacilities}
                style={{
                  padding: '10px 20px',
                  backgroundColor: !isAdmin || !hasFacilities 
                    ? '#9ca3af' 
                    : (canAddMorePitches ? '#3b82f6' : '#f59e0b'),
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: isAdmin && hasFacilities ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {canAddMorePitches ? (
                  <>
                  <span>Add/edit/delete Pitch</span>
                  </>
                ) : (
                  <>
                    <span>🔒</span>
                    <span>Upgrade to Add More Pitches</span>
                  </>
                )}
              </button>
              
              {!isAdmin && (
                <p style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  margin: 0,
                  fontStyle: 'italic'
                }}>
                  Admin access required to add pitches
                </p>
              )}
            </div>

            {/* ===============================================================
                PITCHES LIST
                =============================================================== */}
            {displayPitches.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#6b7280'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏟️</div>
                <p style={{ fontSize: '16px', marginBottom: '8px' }}>
                  {hasFacilities 
                    ? `No pitches configured for ${selectedFacility?.name || 'this facility'}`
                    : 'No pitches configured yet'
                  }
                </p>
                <p style={{ fontSize: '14px' }}>
                  {isAdmin 
                    ? 'Click "Add Pitch from Satellite" to get started'
                    : 'Contact an administrator to add pitches'}
                </p>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {displayPitches.map((pitch) => {
                    const pitchId = normalizePitchId(pitch.id || pitch.pitchId);
                    const currentConfig = pitchSectionConfigurations[pitchId];
                    const configString = convertSatelliteConfigToSettingsFormat(currentConfig);
                    const sectionOption = PITCH_SECTION_OPTIONS.find(opt => opt.id === configString);

                    return (
                      <div
                        key={pitchId}
                        style={{
                          padding: '16px',
                          backgroundColor: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px'
                        }}
                      >
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{
                            display: 'block',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#374151',
                            marginBottom: '8px'
                          }}>
                            Pitch Name {!isAdmin && '(View Only)'}
                          </label>
                          <input
                            type="text"
                            value={pitchNames[pitchId] || ''}
                            onChange={(e) => {
                              if (isAdmin) {
                                onPitchNamesChange({ ...pitchNames, [pitchId]: e.target.value });
                              }
                            }}
                            onBlur={() => {
                              if (isAdmin) {
                                onSave(pitchNames, pitchReferencePoints, pitchSectionConfigurations);
                              }
                            }}
                            disabled={isSaving || !isAdmin}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px',
                              opacity: isSaving ? 0.6 : 1,
                              backgroundColor: isAdmin ? 'white' : '#f3f4f6'
                            }}
                            placeholder={pitch.name || `Pitch ${pitch.pitchNumber || pitchId.replace('pitch-', '')}`}
                          />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '8px'
                          }}>
                            <label style={{
                              fontSize: '14px',
                              fontWeight: '500',
                              color: '#374151'
                            }}>
                              Section Configuration
                            </label>
                            <button
                              onClick={() => openConfigModal(pitch)}
                              disabled={!isAdmin}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: isAdmin ? '#e0f2fe' : '#f3f4f6',
                                color: isAdmin ? '#0369a1' : '#9ca3af',
                                border: `1px solid ${isAdmin ? '#7dd3fc' : '#e5e7eb'}`,
                                borderRadius: '6px',
                                cursor: isAdmin ? 'pointer' : 'not-allowed',
                                fontSize: '12px',
                                fontWeight: '500'
                              }}
                            >
                              Configure
                            </button>
                          </div>
                          <div style={{
                            padding: '12px',
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px'
                            }}>
                              <span style={{ fontSize: '20px' }}>{sectionOption?.icon || '⬜'}</span>
                              <div>
                                <div style={{
                                  fontSize: '14px',
                                  fontWeight: '600',
                                  color: '#374151'
                                }}>
                                  {sectionOption?.name || 'Not configured'}
                                </div>
                                <div style={{
                                  fontSize: '12px',
                                  color: '#6b7280'
                                }}>
                                  {sectionOption?.description || 'No section configuration set'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#374151',
                            marginBottom: '8px'
                          }}>
                            Reference Points {!isAdmin && '(View Only)'}
                          </label>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '8px'
                          }}>
                            {['top', 'bottom', 'left', 'right'].map((position) => (
                              <div key={position}>
                                <label style={{
                                  fontSize: '12px',
                                  color: '#6b7280',
                                  marginBottom: '4px',
                                  display: 'block',
                                  textTransform: 'capitalize'
                                }}>
                                  {position}
                                </label>
                                <input
                                  type="text"
                                  value={localReferencePoints[pitchId]?.[position] || pitchReferencePoints[pitchId]?.[position] || ''}
                                  onChange={(e) => handleReferencePointChange(pitchId, position, e.target.value)}
                                  onBlur={() => handleReferencePointBlur(pitchId, position)}
                                  disabled={isSaving || !isAdmin}
                                  style={{
                                    width: '100%',
                                    padding: '6px 8px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    opacity: isSaving ? 0.6 : 1,
                                    backgroundColor: isAdmin ? 'white' : '#f3f4f6'
                                  }}
                                  placeholder={`e.g., ${position === 'top' ? 'North Goal' : position === 'bottom' ? 'South Goal' : position === 'left' ? 'Car Park' : 'Club House'}`}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: '#f0f9ff',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#0369a1'
                }}>
                  Using {currentPitchCount} of {limits.maxPitches} available pitches on your {limits.displayName} plan.
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <PremiumFeatureModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        featureName={premiumModalConfig.featureName}
        currentTier={premiumModalConfig.currentTier}
        requiredTiers={premiumModalConfig.requiredTiers || []}
        benefits={premiumModalConfig.benefits || []}
      />

      {/* Trial Expired Modal */}
      <TrialExpiredModal
        isOpen={showTrialExpiredModal}
        onClose={() => setShowTrialExpiredModal(false)}
      />

      {showConfigModal && selectedPitchForConfig && (
        <PitchConfigModal
          pitch={selectedPitchForConfig}
          pitchName={pitchNames[normalizePitchId(selectedPitchForConfig.id)]}
          currentConfig={convertSatelliteConfigToSettingsFormat(
            pitchSectionConfigurations[normalizePitchId(selectedPitchForConfig.id)]
          )}
          onConfigUpdate={handleSectionConfigUpdate}
          onClose={closeConfigModal}
        />
      )}

      {showSizeWarning && pendingSizeChange && (
        <PitchSizeWarningModal
          pendingChange={pendingSizeChange}
          onConfirm={() => {
            if (pendingSizeChange) {
              proceedWithSizeChange(
                pendingSizeChange.pitchId,
                pendingSizeChange.sectionConfig
              );
            }
          }}
          onCancel={() => {
            setShowSizeWarning(false);
            setPendingSizeChange(null);
            setSizeWarningConfirmed(false);
          }}
          isConfirmed={sizeWarningConfirmed}
          setIsConfirmed={setSizeWarningConfirmed}
        />
      )}

      {showSuccessModal && successModalData && (
        <SuccessConfirmationModal
          pitchName={successModalData.pitchName}
          configName={successModalData.configName}
          onClose={() => {
            setShowSuccessModal(false);
            setSuccessModalData(null);
            
            // Refresh the page to show updated configuration
            window.location.reload();
          }}
        />
      )}

      {/* Spin animation for loading */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default PitchConfiguration;
